import { useEffect, useState } from "react";

export interface TocItem {
  id: string;
  label: string;
}

interface ArticleTocProps {
  items: TocItem[];
}

export default function ArticleToc({ items }: ArticleTocProps) {
  const [active, setActive] = useState(items[0]?.id ?? "");

  const idKey = items.map((item) => item.id).join(",");

  useEffect(() => {
    const ids = idKey.split(",").filter(Boolean);
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter((node): node is HTMLElement => Boolean(node));
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          setActive(visible[0].target.id);
        }
      },
      { rootMargin: "-120px 0px -60% 0px", threshold: 0 }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [idKey]);

  const goTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <nav aria-label="本文目录" className="rounded-lg border border-background-200 bg-background-100/50 p-5">
      <p className="label-caps text-[11px] text-foreground-500">本文结构</p>
      <ol className="mt-4 space-y-1">
        {items.map((item, index) => (
          <li key={item.id}>
            <button
              type="button"
              onClick={() => goTo(item.id)}
              className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors cursor-pointer ${
                active === item.id
                  ? "bg-background-50 font-medium text-primary-700"
                  : "text-foreground-600 hover:bg-background-50 hover:text-foreground-950"
              }`}
            >
              <span className="label-caps font-heading text-[10px] text-foreground-400">
                {String(index + 1).padStart(2, "0")}
              </span>
              {item.label}
            </button>
          </li>
        ))}
      </ol>
    </nav>
  );
}